import React, { useEffect, useState } from "react";
import { Card, Descriptions, List, Typography, Spin, Alert, Button, Tag, Divider } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import moment from "moment";

const { Title, Text } = Typography;

const PerformanceReviewDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { employeeData } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // If no employee data is passed, show error
  useEffect(() => {
    if (!employeeData) {
      setError("No employee data provided");
    }
  }, [employeeData]);

  // Loading state
  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <Spin size="large" />
        </div>
      </DashboardLayout>
    );
  }

  // Error state
  if (error || !employeeData) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <div style={{ padding: "20px" }}>
          <Alert type="error" message={error || "No employee data available"} showIcon />
          <Button className="btn-back" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const employee = employeeData;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div style={{ padding: "20px" }}>
        <Card
          className="card-professional"
          title={`Performance Review Details - ${employee.revieweeName}`}
          extra={<Button onClick={() => navigate(-1)}>Back</Button>}
        >
          {/* Basic Information Section */}
          <Title level={4}>Basic Information </Title>
          <Descriptions className="descriptions-professional" bordered  column={2} size="small">
           <Descriptions.Item label="Employee Name">
  {employee.revieweeName}
</Descriptions.Item>

<Descriptions.Item label="Employee ID">
  {employee.revieweeId}
</Descriptions.Item>

<Descriptions.Item label="Manager">
  {employee.reviewerName}
</Descriptions.Item> 
           <Descriptions.Item label="Manager ID">{employee.reviewerId}</Descriptions.Item>
            <Descriptions.Item label="Department">{employee.departmentName}</Descriptions.Item>
            <Descriptions.Item label="Department ID">{employee.departmentId}</Descriptions.Item>
            <Descriptions.Item label="Hire Date">
              {moment(employee.hireDate).isValid()
                ? moment(employee.hireDate).format("MM/DD/YYYY")
                : "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Review ID">{employee.id}</Descriptions.Item>
          </Descriptions>

          <Divider />

          {/* Review Period Information */}
          <Title level={4}>Review Period</Title>
          <Descriptions className="descriptions-professional" bordered column={2} size="small">
         <Descriptions.Item label="Period Start">
  {employee.periodStart
    ? moment(employee.periodStart).format("MM/DD/YYYY")
    : "N/A"}
</Descriptions.Item>

<Descriptions.Item label="Period End">
  {employee.periodEnd
    ? moment(employee.periodEnd).format("MM/DD/YYYY")
    : "N/A"}
</Descriptions.Item>
            <Descriptions.Item label="Created On">
              {moment(employee.createdOn).isValid()
                ? moment(employee.createdOn).format("MM/DD/YYYY HH:mm")
                : "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Last Modified">
              {moment(employee.lastModifiedOn).isValid()
                ? moment(employee.lastModifiedOn).format("MM/DD/YYYY HH:mm")
                : "N/A"}
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          {/* Performance Ratings Section */}
          <Title level={4}>Performance Ratings</Title>
          <Descriptions className="descriptions-professional" bordered column={2} size="small">
           <Descriptions.Item label="Overall Accountability Rating">
  {employee.overallAccountabilityGoalRating
    ? employee.overallAccountabilityGoalRating.toFixed(2)
    : "N/A"} /5
</Descriptions.Item>
            <Descriptions.Item label="Overall Performance Factor Rating">
              {employee.overallPerformanceFactorRating?.toFixed(2)}/5
            </Descriptions.Item>
            <Descriptions.Item label="Overall Dynamic Organization Rating">
              {employee.overallDynamicOrganizationRating?.toFixed(2)}/5
            </Descriptions.Item>
            <Descriptions.Item label="Total Performance Rating">
              {employee.totalPerformanceRating?.toFixed(2)}/5
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          {/* Weighted Scores Section */}
          <Title level={4}>Weighted Scores</Title>
          <Descriptions  className="descriptions-professional" bordered column={2} size="small">
            <Descriptions.Item label="Weighted Accountability Score">
              {employee.weightedAccountabilityScore}/50
            </Descriptions.Item>
            <Descriptions.Item label="Weighted Performance Factors Score">
              {employee.weightedPerformanceFactorsScore}/40
            </Descriptions.Item>
            <Descriptions.Item label="Weighted Dynamic Organization Score">
              {employee.weightedDynamicOrganizationScore}/10
            </Descriptions.Item>
            <Descriptions.Item label="Total Performance Score">
              <Text strong>{employee.totalPerformanceScore}/100</Text>
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          {/* Final Rating Section */}
          <Title level={4}>Final Rating</Title>
          <Descriptions className="descriptions-professional" bordered column={2} size="small">
            <Descriptions.Item label="Rating">{employee.rating}/5</Descriptions.Item>
            <Descriptions.Item label="Performance Rating Code">
              <Tag
  style={{
    backgroundColor: 
        employee.performanceRatingCode === "FE" ? "#dff7c0" : 
        employee.performanceRatingCode === "EE" ? "#ecffc7" : 
        employee.performanceRatingCode === "ME" ? "#fef5d3" : "#fc9797",
    color: 
        employee.performanceRatingCode === "FE" ? "#226e1b" : 
        employee.performanceRatingCode === "EE" ? "#549247" : 
        employee.performanceRatingCode === "ME" ? "#fbc400" : "#bd0000",
    border: "none",
    fontWeight: 600
  }}
>
                {employee.performanceRatingCode}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Performance Rating Name">
              {employee.performanceRatingName}
            </Descriptions.Item>
            <Descriptions.Item label="Performance Rating Description">
              {employee.performanceRatingDescription}
            </Descriptions.Item>
          </Descriptions>

          {/* Summary Section */}
          <Divider />
          <Title level={4}>Performance Summary</Title>
          <Card size="small" className="clean-card">
            <Text>
              <strong>Employee:</strong> {employee.revieweeName} achieved a total performance score
              of <strong>{employee.totalPerformanceScore}/100</strong> with a rating of{" "}
              <strong>{employee.performanceRatingName}</strong> ({employee.performanceRatingCode}).{" "}
              {employee.performanceRatingDescription}
            </Text>
          </Card>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default PerformanceReviewDetail;
